import React, { createContext, useReducer, useContext, useMemo } from "react";
import { NavigateFunction } from "react-router-dom";
import { Header } from "semantic-ui-react";

import { ContentItem, CustomThemeReportItem, CustomThemeUserReports } from "../components/Custom";

import {
  apiCustomContentsDtl,
  apiCustomThemeDtl,
  apiCustomThemeUserGroup,
  childrenProps,
  contentsStatus,
  deviceType,
} from "../types";
import {
  getContentPagePath,
  getContentTestPagePath,
  undefinedToBoolean,
  undefinedToNumber,
  undefinedToString,
} from "../util/common.util";

type CustomListInfo<T> = {
  hasData: boolean;
  headerTitle: string;
  dataList: T[];
  filteredDataList?: T[];
  useWide: boolean;
  usePcDivided?: boolean;
  useSpDivided?: boolean;
  useGroup?: boolean;
  useStatus?: boolean;
  useStudyGroupFilter?: boolean;
  studyGroups?: Array<{id: number, name: string}>;
  hasUsersWithoutStudyGroup?: boolean;
  selectedStudyGroupId?: number | null;
  // リストで表示される1行のコンポーネント
  getItemContent: (item: T, deviceType: deviceType, navigate: NavigateFunction, useStatus?: boolean) => JSX.Element;
  // dataListの中からgroupingする際に判定に利用する数字 または 文字列
  getGroupKey?: (item: T) => number | string;
  // グルーピング表示するヘッダー行のコンポーネント
  getGroupContent?: (item: T, deviceType: deviceType, navigate: NavigateFunction) => string | JSX.Element;
  // ネストされたグルーピング用のプロパティ
  useNestedGroup?: boolean;
  // セカンダリグループ（二段階目）のキーを取得する関数
  getSecondaryGroupKey?: (item: T) => number | string;
  // セカンダリグループのヘッダー行のコンポーネント
  getSecondaryGroupContent?: (item: T, deviceType: deviceType, navigate: NavigateFunction) => string | JSX.Element;
  // グループ（トグル）を開いた時に中身を読み込む遅延ロード用のプロパティ
  // useLazyGroup = true の場合、dataList の1要素 = 1グループとして扱い、getLazyGroupContent の結果をトグルを開いた時に初めて描画する
  useLazyGroup?: boolean;
  getLazyGroupContent?: (item: T, deviceType: deviceType) => JSX.Element;
};
const initialState = {
  hasData: false,
  headerTitle: "表示データが見つかりませんでした。",
  dataList: [],
  useWide: false,
  getItemContent: () => {
    return <div>データがありません</div>;
  },
} as CustomListInfo<string>;

const CustomListContext = createContext(
  {} as {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    customListContext: CustomListInfo<any>;
    setStudyGroupFilter: (studyGroupId: number | null) => void;
  }
);

const CustomListDispatchContext = createContext(
  {} as {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    dispatch: CustomListDispatch<any>;
  }
);

export type CustomListDispatch<T> = React.Dispatch<action<T>>;

type actionType = "OPEN_CONTENTS" | "OPEN_DM_REPORTS" | "OPEN_USER_REPORTS" | "CLOSE_CUSTOM_LIST" | "SET_STUDY_GROUP_FILTER";
type action<T> = { type: actionType; customListInfo?: CustomListInfo<T>; studyGroupId?: number | null };

export type dummyItem = {
  themeId: number;
  id: number;
  title: string;
  themeDescription: string;
  header: string;
  contents: string;
};

export const closeCustomList = (): action<string> => {
  return {
    type: "CLOSE_CUSTOM_LIST",
  };
};

export const openContents = (
  data: apiCustomContentsDtl[],
  title: string,
  useStatus?: boolean
): action<apiCustomContentsDtl> => {
  // api側でソートがかからないので、ここでまとめてソート
  const compareOrd = (a: number, b: number) => {
    return a === b ? 0 : a < b ? -1 : 1;
  };
  const sortedData = Array.from(data).sort((a, b) => {
    if (a.theme.id === b.theme.id) {
      return compareOrd(a.contents.ord, b.contents.ord);
    } else {
      return compareOrd(a.theme.ord, b.theme.ord);
    }
  });
  const getItemContent = (
    item: apiCustomContentsDtl,
    deviceType: deviceType,
    navigate: NavigateFunction,
    useStatus?: boolean
  ) => {
    const contentsTrasition = () => {
      navigate(getContentPagePath(item.contents.id, item.contents.categoryAccountOriginalId != undefined));
    };
    const contentsTestTrasition = () => {
      navigate(getContentTestPagePath(item.contents.id, item.contents.categoryAccountOriginalId != undefined));
    };
    return (
      <ContentItem
        id={item.contents.id}
        name={item.contents.name}
        image={item.contents.image}
        description={item.contents.description}
        contentsStatus={undefinedToString(item.contents.historyContentsStatus, "NEW") as contentsStatus}
        requiredTest={item.contents.requiredTest}
        contentsTrasition={contentsTrasition}
        deviceType={deviceType}
        viewCount={undefinedToNumber(item.contents.viewCount, 0)}
        existTest={undefinedToBoolean(item.contents.existTest, false)}
        contentsTestTrasition={contentsTestTrasition}
        categoryName={item.category !== null ? item.category.name : item.career.name}
        themeName={item.theme.name}
        testPass={item.contents.testPass}
        useRecomendTest={true}
        playTime={item.contents.playtime}
        useStatus={useStatus}
        // ドリルダウン側でdivideをコントロールするために固定でlengthとindexを入れる
        length={1}
        index={0}
      />
    );
  };
  // const getGroupKey = (item: apiCustomContentsDtl) => {
  //   return item.theme.id;
  // };
  // const getGroupContent = (item: apiCustomContentsDtl) => {
  //   // 本来はキャッシュから取得したテーマ情報でテーマの各情報をバインドする
  //   return <Header content={item.theme.name} as="h3" />;
  //   // return <Label color="blue" content={item.title} />;
  // };
  return {
    type: "OPEN_CONTENTS",
    customListInfo: {
      hasData: true,
      headerTitle: title,
      dataList: sortedData,
      useWide: false,
      usePcDivided: true,
      useSpDivided: false,
      // リストで表示される1行のコンポーネント
      getItemContent: getItemContent,
      // テーマ名でのグルーピングはしない
      useGroup: false,
      // dataListの中からgroupingする際に判定に利用する数字 または 文字列
      // getGroupKey: getGroupKey,
      // グルーピング表示するヘッダー行のコンポーネント
      // getGroupContent: getGroupContent,
      useStatus: useStatus,
    },
  };
};

export const openDmReports = (datas: apiCustomThemeUserGroup[], title: string): action<apiCustomThemeUserGroup> => {
  // トグルを開いた時に取得したレポート詳細（キー: user.id）。再描画や開閉のたびに再取得しないために保持する
  const reportCache = new Map<number, apiCustomThemeDtl[]>();

  const getItemContent = (item: apiCustomThemeDtl, deviceType: deviceType) => {
    return (
      <CustomThemeReportItem
        id={item.report.id}
        accountOriginalFlg={item.report.accountOriginalFlg}
        categoryId={item.theme.categoryId}
        themeId={item.theme.id}
        userId={item.user.id}
        report={item.report.report}
        status={item.report.status}
        themeName={item.theme.name}
        themeDescription={item.theme.description}
        themeImage={item.theme.image}
        deviceType={deviceType}
      />
    );
  };

  // ユーザー単位でグルーピング
  const getGroupKey = (item: apiCustomThemeUserGroup) => {
    return item.user.id;
  };

  const getGroupContent = (item: apiCustomThemeUserGroup) => {
    const studyGroupDisplay = item.user.studyGroupName ? ` (${item.user.studyGroupName})` : "";
    return (
      <Header
        content={`${item.user.lastName} ${item.user.firstName}${studyGroupDisplay}`}
        as="h3"
        style={{ padding: "0rem 0rem 0rem 0.5rem" }}
      />
    );
  };

  // トグルを開いた時に、そのユーザーのレポート詳細を取得して表示する
  const getLazyGroupContent = (item: apiCustomThemeUserGroup, deviceType: deviceType) => {
    return (
      <CustomThemeUserReports
        key={item.user.id}
        userId={item.user.id}
        reports={item.reports}
        cache={reportCache}
        useDivided={true}
        renderItem={(dtl) => getItemContent(dtl, deviceType)}
      />
    );
  };

  // データをソート（ユーザー名でソート）
  const sortedData = [...datas].sort((a, b) => {
    const userNameA = `${a.user.lastName} ${a.user.firstName}`;
    const userNameB = `${b.user.lastName} ${b.user.firstName}`;
    return userNameA.localeCompare(userNameB, "ja");
  });

  // 受講グループの一覧を作成
  const studyGroupMap = new Map<number, string>();
  let hasUsersWithoutStudyGroup = false;

  datas.forEach((item) => {
    if (item.user.studyGroupId && item.user.studyGroupName) {
      studyGroupMap.set(item.user.studyGroupId, item.user.studyGroupName);
    } else {
      hasUsersWithoutStudyGroup = true;
    }
  });

  const studyGroups = Array.from(studyGroupMap.entries()).map(([id, name]) => ({ id, name }));

  return {
    type: "OPEN_DM_REPORTS",
    customListInfo: {
      hasData: true,
      headerTitle: title,
      dataList: sortedData,
      filteredDataList: sortedData,
      useWide: true,
      useStudyGroupFilter: true,
      studyGroups: studyGroups,
      hasUsersWithoutStudyGroup: hasUsersWithoutStudyGroup,
      selectedStudyGroupId: null,
      // リストで表示される1行のコンポーネント（遅延ロード時は使われない）
      getItemContent: () => <></>,
      // ユーザー単位でグルーピング。レポート詳細はトグルを開いた時に取得する
      useGroup: true,
      useNestedGroup: false,
      getGroupKey: getGroupKey,
      getGroupContent: getGroupContent,
      useLazyGroup: true,
      getLazyGroupContent: getLazyGroupContent,
    },
  };
};

export const openDmUserReports = (datas: apiCustomThemeDtl[], title: string): action<apiCustomThemeDtl> => {
  const getItemContent = (item: apiCustomThemeDtl, deviceType: deviceType) => {
    return (
      <CustomThemeReportItem
        id={item.report.id}
        accountOriginalFlg={item.report.accountOriginalFlg}
        categoryId={item.theme.categoryId}
        themeId={item.theme.id}
        userId={item.user.id}
        report={item.report.report}
        status={item.report.status}
        themeName={item.theme.name}
        themeDescription={item.theme.description}
        themeImage={item.theme.image}
        deviceType={deviceType}
      />
    );
  };
  const getGroupKey = (item: apiCustomThemeDtl) => {
    return item.user.id;
  };
  const getGroupContent = (item: apiCustomThemeDtl) => {
    // 本来はキャッシュから取得したテーマ情報でテーマの各情報をバインドする
    return (
      <Header
        content={`${item.user.lastName} ${item.user.firstName}`}
        as="h3"
        style={{ padding: "0rem 0rem 0rem 0.5rem" }}
      />
    );
  };
  return {
    type: "OPEN_USER_REPORTS",
    customListInfo: {
      hasData: true,
      headerTitle: title,
      dataList: datas,
      useWide: true,
      // リストで表示される1行のコンポーネント
      getItemContent: getItemContent,
      // テーマ名でのグルーピングはしない
      useGroup: true,
      getGroupKey: getGroupKey,
      getGroupContent: getGroupContent,
    },
  };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const customListReducer = (state: CustomListInfo<any>, action: action<any>): CustomListInfo<any> => {
  switch (action.type) {
    case "OPEN_CONTENTS":
      return {
        ...state,
        ...action.customListInfo,
      };
    case "OPEN_DM_REPORTS":
      return {
        ...state,
        ...action.customListInfo,
      };
    case "OPEN_USER_REPORTS":
      return {
        ...state,
        ...action.customListInfo,
      };
    case "SET_STUDY_GROUP_FILTER": {
      if (!state.useStudyGroupFilter || !state.dataList) {
        return state;
      }
      let filteredData;
      if (action.studyGroupId === null) {
        // 全ての受講グループ
        filteredData = state.dataList;
      } else if (action.studyGroupId === 0) {
        // （未分類）（studyGroupId が 0 または未設定）
        filteredData = state.dataList.filter((item: apiCustomThemeDtl) => !item.user.studyGroupId);
      } else {
        // 特定の受講グループ
        filteredData = state.dataList.filter((item: apiCustomThemeDtl) => item.user.studyGroupId === action.studyGroupId);
      }
      return {
        ...state,
        filteredDataList: filteredData,
        selectedStudyGroupId: action.studyGroupId,
      };
    }
    case "CLOSE_CUSTOM_LIST":
      return initialState;
    default:
      return state;
  }
};

export const setStudyGroupFilter = (studyGroupId: number | null): action<apiCustomThemeDtl> => {
  return {
    type: "SET_STUDY_GROUP_FILTER",
    studyGroupId,
  };
};

export const CustomListProvider = (props: childrenProps) => {
  const [contextData, dispatch] = useReducer(customListReducer, initialState);
  
  const setStudyGroupFilterHandler = (studyGroupId: number | null) => {
    dispatch(setStudyGroupFilter(studyGroupId));
  };

  const customListContext = useMemo(
    () => ({
      customListContext: contextData,
      setStudyGroupFilter: setStudyGroupFilterHandler,
    }),
    [contextData]
  );

  const customListDispatch = useMemo(
    () => ({
      dispatch,
    }),
    []
  );

  return (
    <CustomListContext.Provider value={customListContext}>
      <CustomListDispatchContext.Provider value={customListDispatch} {...props} />
    </CustomListContext.Provider>
  );
};

export const useCustomListContext = () => {
  return useContext(CustomListContext);
};

export const useCustomListDispatch = () => {
  return useContext(CustomListDispatchContext);
};
